import React, { useState } from "react";
import { X, Sparkles, AlertTriangle } from "lucide-react";

/**
 * iter65 — "Regenerate with Director Input" modal.
 *
 * Reusable for both storyboard image regen and Sora video regen. The
 * parent supplies an async `onConfirm(note, uploadedRefIds)` handler.
 *
 * UX rules:
 *   - Operator must type a non-empty note before the Regenerate button enables.
 *   - The modal explicitly says it does NOT surgically edit the original;
 *     it generates a NEW version using the correction.
 *   - For paid (video) regen, the parent should set
 *     `costPreview="$0.40 (4s sora-2)"` so the operator sees the cost
 *     BEFORE confirming. The cost-modal contract from iter62 is enforced
 *     on the backend (HTTP 400 cost_modal_confirmation_required if the
 *     parent forgets to send confirmed=true).
 */
export default function RegenerateWithNotesModal({
    open, target, shotId, costPreview, busy, onConfirm, onClose,
}) {
    const [note, setNote] = useState("");
    const [uploadedRefs, setUploadedRefs] = useState("");
    const isPaid = target === "video";
    if (!open) return null;
    const submit = async () => {
        const refs = uploadedRefs.split(",").map(s => s.trim()).filter(Boolean);
        await onConfirm(note.trim(), refs);
    };
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
            data-testid={`regen-modal-${target}-${shotId}`}
        >
            <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-xl p-5 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-300" />
                            Regenerate with Director Input
                        </h3>
                        <p className="text-[11px] text-zinc-500 mt-1">
                            Shot <span className="font-mono">{shotId}</span> · target {target}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-zinc-500 hover:text-zinc-200"
                        data-testid={`regen-modal-close-${shotId}`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="rounded-md border border-amber-700/40 bg-amber-950/20 p-3 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-amber-200/90">
                        This regenerates a NEW {target === "video" ? "clip" : "image"}{" "}
                        using your correction. It does NOT surgically edit the existing one.
                        Original v1 stays saved. The new version (v2/v3/...) appears
                        alongside it — you can compare and approve your preferred version.
                    </div>
                </div>

                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Raw director note
                    </span>
                    <textarea
                        data-testid={`regen-modal-note-${shotId}`}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder='e.g. "change clothes to black, rest same" or "dialogue was half cut, make visual-only and move line to VO overlay"'
                        rows={4}
                        className="w-full mt-1 bg-black border border-zinc-800 rounded-sm px-2 py-2 text-xs text-zinc-200 placeholder:text-zinc-600"
                    />
                </label>

                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Optional reference asset IDs (comma-separated)
                    </span>
                    <input
                        data-testid={`regen-modal-refs-${shotId}`}
                        type="text"
                        value={uploadedRefs}
                        onChange={(e) => setUploadedRefs(e.target.value)}
                        placeholder="asset-logo-upload-xyz, asset-background-abc"
                        className="w-full mt-1 bg-black border border-zinc-800 rounded-sm px-2 py-2 text-xs font-mono text-zinc-300 placeholder:text-zinc-600"
                    />
                    <span className="text-[10px] text-zinc-600 mt-1 block">
                        Paste IDs from Reference Lock / Asset Refinement. The Prompt Director references them by ID — it does NOT invent their contents.
                    </span>
                </label>

                {isPaid && costPreview && (
                    <div
                        data-testid={`regen-modal-cost-${shotId}`}
                        className="text-[12px] text-amber-200 border border-amber-700/40 bg-amber-950/20 rounded-md px-3 py-2"
                    >
                        Estimated cost: <strong>{costPreview}</strong>. Counts toward the daily Sora cost cap.
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid={`regen-modal-confirm-${shotId}`}
                        onClick={submit}
                        disabled={busy || !note.trim()}
                        className="text-xs px-3 py-1.5 rounded-sm bg-amber-500/80 hover:bg-amber-500 text-zinc-950 font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        {busy ? "Regenerating…" : "Regenerate"}
                    </button>
                </div>
            </div>
        </div>
    );
}
